import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import { AlertLevel } from '@/types';
import { captureException } from '@/utils/sentry';
import {
  CombinedActionSheet,
  JumpToFn,
} from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import {
  getVisibleOptions,
  LoyaltyEnrollmentFormContent,
} from '@/components/loyalty/loyalty-enrollment-form-content';
import { LoyaltyCompletionContent } from '@/components/loyalty/loyalty-completion-content';
import {
  LoyaltyEnrollmentFormConfig,
  LoyaltyQuestionType,
  LoyaltySelectQuestion,
} from '@/components/loyalty/loyalty-enrollment-form-types';
import { LoyaltyTermsContent } from '@/components/loyalty/loyalty-terms-content';
import { RadioButtonGroup } from '@/components/radio-button-group';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE } from '@/styles';
import { ScrollView, TextStyle, useWindowDimensions } from 'react-native';
import { Type as ToastType } from '@/components/toast';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSubmitLoyaltyEnrollmentForm } from '@/hooks/use-submit-loyalty-enrollment-form';
import { useSubmitLoyaltyTermsDecision } from '@/hooks/use-submit-loyalty-terms-decision';
import { useToasts } from '@/contexts/toast-context';

const MODAL_WIDTH = 520;

export enum LoyaltyTermsRoute {
  Completion = 'COMPLETION',
  Form = 'FORM',
  FormOptions = 'FORM_OPTIONS',
  Terms = 'TERMS',
}

interface Props {
  bannerImageUrl?: string | null;
  bannerText?: string | null;
  completionText?: string | null;
  enrollmentFormConfig?: LoyaltyEnrollmentFormConfig | null;
  enrollmentUUID: string | null;
  // didResolve is true when the enrollment was accepted or declined and false
  // when the user just dismissed
  onClose: (didResolve: boolean) => void;
  termsAcceptanceDisclaimer?: string | null;
  termsSummary?: string | null;
  termsUrl?: string | null;
  termsUUID: string | null;
}

export const LoyaltyTermsFlow: React.FC<Props> = ({
  bannerImageUrl,
  bannerText,
  completionText,
  enrollmentUUID,
  enrollmentFormConfig,
  onClose,
  termsAcceptanceDisclaimer,
  termsSummary,
  termsUUID,
  termsUrl,
}) => {
  const { user } = useAuthenticatedUserContext();
  const { isSubmitting, submitLoyaltyTermsDecision } =
    useSubmitLoyaltyTermsDecision();
  const { isSubmitting: isSubmittingForm, submitLoyaltyEnrollmentForm } =
    useSubmitLoyaltyEnrollmentForm();
  const { showErrorToast, showToast } = useToasts();
  const [styles] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const { height: windowHeight } = useWindowDimensions();

  // Tracked via ref instead of state so it doesn't cause a re-render
  const didResolveRef = useRef(false);

  const [formAnswers, setFormAnswers] = useState<Record<string, string>>({});
  const [selectedFormQuestion, setSelectedFormQuestion] =
    useState<LoyaltySelectQuestion | null>(null);

  const questions = enrollmentFormConfig?.questions ?? [];

  const setFormAnswer = (key: string, value: string) =>
    setFormAnswers((current) => {
      const next = { ...current, [key]: value };
      const cleared = new Set([key]);
      const pending = [key];

      // Cascade-clears answers for dependent questions
      while (pending.length) {
        const parentKey = pending.pop();
        for (const question of questions) {
          if (
            question.type === LoyaltyQuestionType.Select &&
            question.parent_key === parentKey &&
            !cleared.has(question.key)
          ) {
            cleared.add(question.key);
            pending.push(question.key);
            delete next[question.key];
          }
        }
      }

      return next;
    });

  const handleDecision = async (
    isAccepted: boolean,
    {
      close,
      jumpTo,
    }: { close: () => void; jumpTo: JumpToFn<LoyaltyTermsRoute> },
    email?: string,
  ) => {
    if (!enrollmentUUID || !termsUUID || isSubmitting) {
      return;
    }

    try {
      const result = await submitLoyaltyTermsDecision({
        email,
        isAccepted,
        loyaltyProgramTermsUUID: termsUUID,
        supplierLoyaltyProgramRestaurantUUID: enrollmentUUID,
      });

      if (!result) {
        showErrorToast();
        return;
      }

      if (result.alert_level === AlertLevel.Positive) {
        didResolveRef.current = true;

        if (isAccepted) {
          jumpTo(
            questions.length
              ? LoyaltyTermsRoute.Form
              : LoyaltyTermsRoute.Completion,
          );
        } else {
          showToast({
            content: i18n.t('loyalty.terms_declined'),
            type: ToastType.POSITIVE,
          });
          close();
        }
        return;
      }

      // Non-positive: show the server's alert_message and keep the sheet open
      // so the user can correct (e.g. EMPLOYEE_EMAIL_REQUIRED)
      showToast({
        content: result.alert_message ?? i18n.t('toast.standard_error'),
        type:
          result.alert_level === AlertLevel.Warning
            ? ToastType.WARNING
            : ToastType.NEGATIVE,
      });
    } catch (error) {
      captureException(error);
      showErrorToast();
    }
  };

  const handleSubmitForm = async (jumpTo: JumpToFn<LoyaltyTermsRoute>) => {
    if (!enrollmentUUID || isSubmittingForm) {
      return;
    }

    try {
      const result = await submitLoyaltyEnrollmentForm({
        responses: questions.map((question) => ({
          key: question.key,
          value: formAnswers[question.key]?.trim() ?? '',
        })),
        supplierLoyaltyProgramRestaurantUUID: enrollmentUUID,
      });

      if (!result) {
        showErrorToast();
        return;
      }

      if (result.alert_level === AlertLevel.Positive) {
        jumpTo(LoyaltyTermsRoute.Completion);
        return;
      }

      showToast({
        content: result.alert_message ?? i18n.t('toast.standard_error'),
        type: ToastType.NEGATIVE,
      });
    } catch (error) {
      captureException(error);
      showErrorToast();
    }
  };

  return (
    <CombinedActionSheet
      animateTransitions
      contentStyle={[
        styles.content,
        isSmallLayout ? { maxHeight: windowHeight * 0.85 } : null,
      ]}
      // Each route scrolls its own body and pins its own actions, so the
      // default sticky header would nest a second scroll view on desktop
      hasStickyHeader={false}
      initialRoute={LoyaltyTermsRoute.Terms}
      modalStyle={styles.modal}
      onClose={() => onClose(didResolveRef.current)}
      renderScene={({ close, jumpTo, routeKey }) => {
        switch (routeKey) {
          case LoyaltyTermsRoute.Form:
            return enrollmentFormConfig ? (
              <LoyaltyEnrollmentFormContent
                answers={formAnswers}
                config={enrollmentFormConfig}
                isSubmitting={isSubmittingForm}
                onAnswerChange={setFormAnswer}
                onOpenOptions={(question) => {
                  setSelectedFormQuestion(question);
                  jumpTo(LoyaltyTermsRoute.FormOptions);
                }}
                onSubmit={() => handleSubmitForm(jumpTo)}
              />
            ) : null;
          case LoyaltyTermsRoute.FormOptions: {
            if (!selectedFormQuestion) {
              return null;
            }

            const options = getVisibleOptions(
              selectedFormQuestion,
              formAnswers,
            ).map((option) => ({
              label: option.label ?? option.value,
              value: option.value,
            }));
            return (
              <ScrollView
                contentContainerStyle={[
                  styles.optionsContent,
                  { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
                ]}
                style={styles.optionsScroll}
              >
                <RadioButtonGroup
                  baseStyle={styles.optionsGroup}
                  onChange={(value) => {
                    setFormAnswer(selectedFormQuestion.key, value);
                    jumpTo(LoyaltyTermsRoute.Form);
                  }}
                  options={options}
                  selectedValue={formAnswers[selectedFormQuestion.key]}
                  title={selectedFormQuestion.label}
                  titleStyle={styles.optionsTitle}
                />
              </ScrollView>
            );
          }
          case LoyaltyTermsRoute.Completion:
            return (
              <LoyaltyCompletionContent
                bannerImageUrl={bannerImageUrl}
                completionText={completionText}
                onDone={close}
              />
            );
          case LoyaltyTermsRoute.Terms:
            return (
              <LoyaltyTermsContent
                bannerImageUrl={bannerImageUrl}
                bannerText={bannerText}
                isSubmitting={isSubmitting}
                onAccept={(email) =>
                  handleDecision(true, { close, jumpTo }, email)
                }
                onDecline={() => handleDecision(false, { close, jumpTo })}
                termsAcceptanceDisclaimer={termsAcceptanceDisclaimer}
                termsSummary={termsSummary}
                termsUrl={termsUrl}
                userEmail={user.contact_info.email}
              />
            );
        }
      }}
      routes={{
        [LoyaltyTermsRoute.Completion]: { previousKey: null },
        [LoyaltyTermsRoute.Form]: { previousKey: null, showCloseButton: false },
        [LoyaltyTermsRoute.FormOptions]: {
          dismissKey: LoyaltyTermsRoute.Form,
          modalHeaderStyle: styles.optionsHeader,
          previousKey: LoyaltyTermsRoute.Form,
          showCloseButton: false,
          showSmallLayoutBackButton: true,
        },
        [LoyaltyTermsRoute.Terms]: { previousKey: null },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    paddingBottom: 0,
    paddingHorizontal: 0,
  },
  modal: {
    maxWidth: MODAL_WIDTH,
  },
  optionsContent: {
    paddingHorizontal: DEFAULT_PADDING,
  },
  optionsGroup: {
    borderTopWidth: 0,
    paddingBottom: 0,
    paddingTop: 0,
  },
  optionsHeader: {
    // Add top padding since the close button isn't enabled
    paddingTop: SCREEN_PADDING_LARGE,
  },
  optionsScroll: {
    flexShrink: 1,
  },
  optionsTitle: {
    ...(theme.sk.typography.heading.sm as TextStyle),
    color: theme.sk.color.text.primary,
    marginBottom: theme.sk.spacing.spaceSmall,
    textTransform: 'none',
  },
}));
